<template>
  <WdsStack v-if="hasStaffingAgency" direction-column class="staffing-agency-container">
    <WdsStack direction-row align-center justify-space-between gap-sm>
      <WdsStack direction-row align-center gap-sm>
        <WdsHeading type="title4">
          {{ viewingAsClient ? $t('Staffing agency attribution') : $t('Placement terms') }}
        </WdsHeading>

        <template v-if="recruiterManagesWorkers">
          <WdsLabel subtle>
            <WdsStack direction-row align-center gap-custom="4px">
              <WdsIcon name="group" :size="12" />
              <WdsText small>{{ $t('Managed worker') }}</WdsText>
            </WdsStack>
          </WdsLabel>

          <WdsTooltip v-if="viewingAsClient" top>
            <WdsIcon name="info-filled" class="tw-text-gray-500" :size="18" />
            <template #tooltip-content>
              <WdsStack direction-column gap-sm>
                <div>
                  {{
                    $t(
                      "Managed Worker - Staffing Agency will be the Contracted Party and manage this worker's contract and payments",
                    )
                  }}
                </div>
              </WdsStack>
            </template>
          </WdsTooltip>
        </template>
      </WdsStack>

      <WdsButtonGhost
        v-if="viewingAsClient && user.hasPermission('manage-contracts') && !supplierIsCompany"
        data-test="staffing-agency-edit-button"
        sm
        @click="showAttributeRecruiterModal = true"
      >
        <WdsStack align-center gap-sm>
          <WdsIcon name="edit" :size="16" />
          {{ $t('Edit') }}
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>

    <div v-if="loading" class="tw-flex tw-justify-center tw-py-4">
      <WdsSpinner :size="24" indeterminate />
    </div>

    <WdsAlert v-else-if="error" error class="tw-mt-2">
      {{ $t('Failed to load staffing agency information') }}
    </WdsAlert>

    <div
      v-else-if="recruiterName || ownershipPeriodText || additiveFeesText || markupText"
      class="tw-mt-4"
    >
      <!-- Main staffing agency info -->
      <div class="tw-grid tw-grid-cols-3 tw-gap-3">
        <template v-if="recruiterName">
          <WdsText class="tw-font-medium">
            <span class="tw-text-gray-800">{{ $t('Attributed') }}</span>
          </WdsText>
          <WdsText data-test="staffing-agency-attributed-name" class="tw-text-gray-900 tw-col-span-2">
            {{ recruiterName }}
          </WdsText>
        </template>

        <template v-if="ownershipPeriodText">
          <WdsText class="tw-font-medium">
            <span class="tw-text-gray-800">{{ $t('Ownership period') }}</span>
          </WdsText>
          <WdsText class="tw-text-gray-900 tw-col-span-2">{{ ownershipPeriodText }}</WdsText>
        </template>

        <WdsText v-if="additiveFeesText" class="tw-font-medium">
          <span class="tw-text-gray-800">{{ $t('Fee') }}</span>
        </WdsText>
        <WdsText v-if="additiveFeesText" class="tw-text-gray-900 tw-col-span-2">
          {{ additiveFeesText }}
        </WdsText>

        <template v-if="markupText">
          <WdsText class="tw-font-medium">
            <span class="tw-text-gray-800">{{ $t('Markup') }}</span>
          </WdsText>
          <WdsStack direction-column gap-custom="2px" class="tw-col-span-2">
            <WdsText class="tw-text-gray-900">{{ markupText }}</WdsText>
            <WdsText
              v-if="clientName && supplierName"
              small
              class="tw-text-gray-500"
            >
              {{ $t('Between :client and :supplier', { client: clientName, supplier: supplierName }) }}
            </WdsText>
          </WdsStack>
        </template>
      </div>
    </div>

    <!-- Reachable only when the attribution has no name and no terms data;
         the outer v-if guarantees an attribution exists, so the copy must
         not claim otherwise. -->
    <WdsAlert v-else data-test="staffing-agency-empty-alert" info class="tw-mt-2">
      {{ $t('No staffing agency details available for this hire') }}
    </WdsAlert>
  </WdsStack>

  <AttributeRecruiterModal
    v-model:open="showAttributeRecruiterModal"
    :hire-id="hireId"
    @close="showAttributeRecruiterModal = false"
    @save="handleAttributeRecruiterSave"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import moment from 'moment'
import {
  WdsAlert,
  WdsButtonGhost,
  WdsHeading,
  WdsIcon,
  WdsLabel,
  WdsSpinner,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { graphql, useFragment } from '@/gql'
import { FeeApplication } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { user } from '@/composables/useUser'
import AttributeRecruiterModal from '@/components/Hire/AttributeRecruiterModal.vue'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'

const props = defineProps<{
  hireId: string
}>()

const { isCompany } = useAccountCapabilities()
const { viewerIsSupplier } = useHireRecruiter(props.hireId)
const viewingAsClient = computed(() => isCompany.value && !viewerIsSupplier.value)

const showAttributeRecruiterModal = ref(false)

const HireStaffingAgencyFragment = graphql(`
  fragment HireStaffingAgencyFields on Hire {
    id
    recruiterManagesWorkers
    fees {
      id
      application
      formattedRate
    }
    markupFee {
      id
      formattedRate
    }
    recruiterOwnershipDays
    recruiterOwnershipStartDate
    recruiter {
      id
      name
    }
    supplier {
      __typename
      id
      name
    }
    company {
      id
      name
    }
  }
`)

const GET_HIRE_RECRUITER_DETAILS = graphql(`
  query GetHireStaffingAgency($id: ID!) {
    hire(id: $id) {
      ...HireStaffingAgencyFields
    }
  }
`)

const { result, loading, error, refetch } = useQuery(
  GET_HIRE_RECRUITER_DETAILS,
  () => ({ id: props.hireId }),
  {
    clientId: 'publicApi',
  },
)

const handleAttributeRecruiterSave = async () => {
  showAttributeRecruiterModal.value = false
  await refetch()
}

const hire = computed(() => useFragment(HireStaffingAgencyFragment, result.value?.hire))

const hasStaffingAgency = computed(() => !!hire.value?.recruiter || !!hire.value?.supplier)

// Company-as-supplier attribution is owned by the relationship between the
// client and the supplier company — the client cannot edit it on a per-hire basis.
const supplierIsCompany = computed(() => hire.value?.supplier?.__typename === 'Company')

const recruiterName = computed(() => hire.value?.recruiter?.name ?? hire.value?.supplier?.name ?? '')

const recruiterManagesWorkers = computed(() => hire.value?.recruiterManagesWorkers ?? false)

const additiveFeesText = computed(() =>
  hire.value?.fees
    .filter((fee) => fee.application === FeeApplication.Additive)
    .map((fee) => fee.formattedRate)
    .join(' + '),
)

// The MSP markup the resolver decided to expose. Null for non-MSP viewers
// (including the client company that owns the hire — they intentionally
// can't see this) or when no markup applies.
const markupText = computed(() => hire.value?.markupFee?.formattedRate ?? null)

const clientName = computed(() => hire.value?.company?.name ?? '')
const supplierName = computed(() => hire.value?.supplier?.name ?? '')

const ownershipPeriodText = computed(() => {
  const totalDays = hire.value?.recruiterOwnershipDays
  const startDate = hire.value?.recruiterOwnershipStartDate

  if (!totalDays || !startDate) {
    return null
  }

  const endDate = moment(startDate).add(totalDays, 'days')
  const formattedEndDate = endDate.format('MMM D, YYYY')
  const hasEnded = moment().isAfter(endDate)

  if (hasEnded) {
    return $t('Ended on :date', { date: formattedEndDate })
  }

  return $t(':totalDays days (until :endDate)', {
    totalDays,
    endDate: formattedEndDate,
  })
})
</script>

<style lang="scss" scoped>
.staffing-agency-container {
  @apply tw-bg-white;
  @apply tw-rounded;
  @apply tw-p-3;
  @apply tw-border-[1px];
  @apply tw-border-gray-200;
}
</style>
